import DOMPurify from 'dompurify';

/**
 * Sanitizes user input to prevent XSS attacks.
 * @param {string} input - The user input to sanitize
 * @returns {string} - The sanitized input
 */
export const sanitizeInput = (input) => {
  // Handle null, undefined or non-string inputs
  if (input === null || input === undefined || typeof input !== 'string') {
    return '';
  }
  
  // Trim the input and sanitize it with DOMPurify
  const trimmed = input.trim();
  return DOMPurify.sanitize(trimmed, { 
    ALLOWED_TAGS: [], // Don't allow any HTML tags
    ALLOWED_ATTR: []  // Don't allow any HTML attributes
  });
};

/**
 * Sanitizes multiline text input to prevent XSS attacks while preserving
 * spaces, linebreaks, and formatting.
 * @param {string} input - The multiline text input to sanitize
 * @returns {string} - The sanitized input with spaces and linebreaks preserved
 */
export const sanitizeMultilineText = (input) => {
  // Handle null, undefined or non-string inputs
  if (input === null || input === undefined || typeof input !== 'string') {
    return '';
  }
  
  // Sanitize with DOMPurify but don't trim to preserve spaces
  return DOMPurify.sanitize(input, { 
    ALLOWED_TAGS: [], // Don't allow any HTML tags
    ALLOWED_ATTR: []  // Don't allow any HTML attributes
  });
};

/**
 * Sanitizes HTML content for safe display.
 * Use this for cases where you want to allow some HTML but sanitize it.
 * @param {string} html - The HTML content to sanitize
 * @returns {string} - The sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (html === null || html === undefined || typeof html !== 'string') {
    return '';
  }
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  });
};

/**
 * Sanitizes numeric input (allows only digits and decimal point)
 * @param {string} input - The numeric input to sanitize
 * @returns {string} - The sanitized numeric value
 */
export const sanitizeNumeric = (input) => {
  if (input === null || input === undefined) {
    return '';
  }
  
  // Convert to string if it's a number
  const stringValue = typeof input === 'number' ? input.toString() : String(input);
  
  // First sanitize with DOMPurify to prevent XSS, then strip non-numeric chars
  const sanitized = DOMPurify.sanitize(stringValue, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return sanitized.replace(/[^\d.]/g, '');
};

/**
 * Sanitizes BSB input (allows only digits, spaces, and hyphens)
 * @param {string} input - The BSB input to sanitize
 * @returns {string} - The sanitized BSB value
 */
export const sanitizeBsb = (input) => {
  if (input === null || input === undefined) {
    return '';
  }
  
  const stringValue = String(input);
  const sanitized = DOMPurify.sanitize(stringValue, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return sanitized.replace(/[^\d\s-]/g, '');
};

/**
 * Sanitizes name inputs (allows only letters and spaces)
 * @param {string} input - The name input to sanitize
 * @returns {string} - The sanitized name
 */
export const sanitizeName = (input) => {
  if (input === null || input === undefined) {
    return '';
  }
  
  const stringValue = String(input);
  const sanitized = DOMPurify.sanitize(stringValue, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return sanitized.replace(/[^a-zA-Z\s]/g, '');
};

/**
 * Sanitizes account number input (allows only digits and spaces)
 * @param {string} input - The account number input to sanitize
 * @returns {string} - The sanitized account number
 */
export const sanitizeAccountNumber = (input) => {
  if (input === null || input === undefined) {
    return '';
  }
  
  const stringValue = String(input);
  const sanitized = DOMPurify.sanitize(stringValue, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return sanitized.replace(/[^\d\s]/g, '');
};
